import React, { useEffect, useContext, useState, useCallback, useMemo } from 'react';
import { CircularProgress } from '@material-ui/core';
import clsx from 'clsx';
import { DialogResponse, DialogResponseCode, DialogType, modeUIContext, setIsEditingForm, showDialog, showSuccessNotification }
    from '@moderepo/mode-ui-state';
import { AsanaSettings, EditableSupportedTicketSystems, JiraSettings }
    from '@moderepo/mode-apis';
import { getAgentFeaturePermissions, RitzAppSettings } from '@moderepo/mode-internal-apis';
import { capitalizeAll, capitalizeFirst } from '@moderepo/mode-app-components';
import { ritzDataContext, selectAppSettings, selectLoggedInAgent, selectProjectAgent, selectProjectById }
    from '@moderepo/mode-data-state-ritz';
import { fetchRobotCloudProjectSettings, rcDataContext, selectRobotCloudProjectSettings, updateRobotCloudProjectSettings }
    from '@moderepo/mode-data-state-robot-cloud';
import { useRitzTranslation } from '../../utils/customHooks';
import { RCTicketSystemSettingsComp, RCTicketSystemSettingsCompProps, RCTicketSystemSettingsUpdatableInputs } from '../../components/robot-cloud';


interface RCProjectSettingErrors {
    salesforceClientId?: string;
    salesforceClientSecret?: string;
    salesforceUsername?: string;
    salesforcePassword?: string;
    salesforceMappingObject?: string;
    salesforceMappingDescription?: string;
    salesforceMappingSeverity?: string;
    salesforceMappingOriginValue?: string;
}


export interface RCTicketSystemSettingsContainerProps {
    readonly projectId: number;
}

export const RCTicketSystemSettingsContainer: React.FC<RCTicketSystemSettingsContainerProps> = (props: RCTicketSystemSettingsContainerProps) => {
    const { projectId } = props;

    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const loggedInAgent = selectLoggedInAgent(dataState);
    const project = selectProjectById(dataState, projectId);
    const projectAgent = loggedInAgent ? selectProjectAgent(dataState, project?.id, loggedInAgent.id) : undefined;
    const appSettings = selectAppSettings(dataState) as RitzAppSettings;
    const permissions = getAgentFeaturePermissions(projectAgent);
    const rcProjectSettings = selectRobotCloudProjectSettings(rcDataState, projectId);
    const { trans } = useRitzTranslation(project);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [rcProjectSettingInputErrors, setRCProjectSettingInputErrors] = useState<RCProjectSettingErrors>({ });


    useEffect(() => {
        if (!rcProjectSettings) {
            rcDataDispatch(fetchRobotCloudProjectSettings(projectId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [rcProjectSettings, projectId, rcDataDispatch]);


    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY so that we can enable this component
        setIsEditing(value);
    }, [uiDispatch]);


    /**
     * end editing mode
     */
    const closeEditing = useCallback(() => {
        // Clear errors
        setRCProjectSettingInputErrors({ });

        onChangeEditingMode(false);
    }, [onChangeEditingMode]);


    const onCancelEdit = useCallback(() => {
        closeEditing();
    }, [closeEditing]);



    /**
     * On project settings input change
     */
    const onRobotCloudProjectInfoFormInputChange = useCallback(() => {
        // do nothing
    }, []);


    /**
     * Handler for when the user click Save input from the Project Settings
     */
    const onRobotCloudProjectInfoFormSubmit = useCallback(async (inputs: RCTicketSystemSettingsUpdatableInputs): Promise<boolean> => {

        if (!project || !rcProjectSettings) {
            return Promise.resolve(false);
        }

        const newInputErrors: RCProjectSettingErrors = { };

        // If the user provided settings for Salesforce, validate the inputs for Salesforce
        if (inputs.salesforce) {
            if (!inputs.salesforce.clientId) {
                newInputErrors.salesforceClientId = 'error.input.generic.empty';
            }
            if (!inputs.salesforce.clientSecret) {
                newInputErrors.salesforceClientSecret = 'error.input.generic.empty';
            }
            if (!inputs.salesforce.username) {
                newInputErrors.salesforceUsername = 'error.input.generic.empty';
            }
            if (!inputs.salesforce.password) {
                newInputErrors.salesforcePassword = 'error.input.generic.empty';
            }

            // Mappings are OPTIONAL however, if the user set a value for one of the fields, he has to provide values for all fields.
            if (inputs.salesforce.mappingObject || inputs.salesforce.mappingDescription
                || inputs.salesforce.mappingSeverity || inputs.salesforce.mappingOriginValue) {
                if (!inputs.salesforce.mappingObject) {
                    newInputErrors.salesforceMappingObject = 'error.input.generic.empty';
                }
                if (!inputs.salesforce.mappingDescription) {
                    newInputErrors.salesforceMappingDescription = 'error.input.generic.empty';
                }
                if (!inputs.salesforce.mappingSeverity) {
                    newInputErrors.salesforceMappingSeverity = 'error.input.generic.empty';
                }
                if (!inputs.salesforce.mappingOriginValue) {
                    newInputErrors.salesforceMappingOriginValue = 'error.input.generic.empty';
                }
            }
        }

        if (inputs.asana) {
            // TODO Add validation for Asana settings
        }

        if (inputs.jira) {
            // TODO Add validation for Jira settings
        }


        // Update error state
        setRCProjectSettingInputErrors(newInputErrors);

        // check if there are errors by counting the number of errors fields in the newInputErrors object
        const hasErrors = Object.values(newInputErrors).length > 0;

        if (!hasErrors) {

            // check if settings changed. If yes, make API call to update.
            const salesforceSettings: EditableSupportedTicketSystems['salesForceSettings'] = inputs.salesforce ? {
                clientId    : inputs.salesforce.clientId as string,
                clientSecret: inputs.salesforce.clientSecret as string,
                username    : inputs.salesforce.username as string,
                password    : inputs.salesforce.password as string,
                mapping     : (inputs.salesforce.mappingObject && inputs.salesforce.mappingDescription
                            && inputs.salesforce.mappingSeverity && inputs.salesforce.mappingOriginValue)
                    ? {
                        sObject    : inputs.salesforce.mappingObject as string,
                        description: inputs.salesforce.mappingDescription as string,
                        severity   : inputs.salesforce.mappingSeverity as string,
                        originValue: inputs.salesforce.mappingOriginValue as string,
                    } : {
                        sObject: '', description: '', severity: '', originValue: '',
                    },
            } : {
                clientId: '', clientSecret: '', username: '', password: '', mapping: {},
            };

            const asanaSettings: AsanaSettings | undefined = inputs.asana ? {
            } : undefined;

            const jiraSettings: JiraSettings | undefined = inputs.jira ? {
            } : undefined;


            const currentSalesforceSettings = rcProjectSettings.supportedTicketSystems.salesForceSettings;
            const currentAsanaSettings = rcProjectSettings.supportedTicketSystems.asanaSettings;
            const currentJiraSettings = rcProjectSettings.supportedTicketSystems.jiraSettings;

            const settingChanged = (currentSalesforceSettings && !inputs.salesforce)                                      // Salesforce removed
                || (inputs.salesforce && JSON.stringify(currentSalesforceSettings) !== JSON.stringify(salesforceSettings))// Salesforce changed
                || (currentAsanaSettings && !inputs.asana)                                                                // Asana removed
                || (inputs.asana && JSON.stringify(currentAsanaSettings) !== JSON.stringify(asanaSettings))               // Asana settings changed
                || (currentJiraSettings && !inputs.jira)                                                                  // Jira removed
                || (inputs.jira && JSON.stringify(currentJiraSettings) !== JSON.stringify(jiraSettings));                 // Jira settings changed


            // Only make API call if setting changed
            if (settingChanged) {

                const onConfirmed = async (): Promise<boolean> => {
                    try {
                        await rcDataDispatch(updateRobotCloudProjectSettings(project.id, {
                            supportedTicketSystems: {
                                salesForceSettings: salesforceSettings || {},
                                asanaSettings,
                                jiraSettings,
                            },
                        }));
                        uiDispatch(showSuccessNotification('notif.generic_item_updated_successfully', { term: 'label.project' }));

                        // API call success, return true and close editing
                        closeEditing();
                        return Promise.resolve(true);
                    } catch (error) {
                        // API call failed, return FALSE
                        return Promise.resolve(false);
                    }
                };

                if ((currentSalesforceSettings && !inputs.salesforce)
                    || (currentAsanaSettings && !inputs.asana)
                    || (currentJiraSettings && !inputs.jira)) {

                    // The user removed one of the settings, confirm if he want to continue before removing it
                    uiDispatch(showDialog({
                        type          : DialogType.CONFIRM,
                        title         : 'dialog.confirm_remove_ticket_system.title',
                        message       : 'dialog.confirm_remove_ticket_system.message',
                        positiveButton: 'label.continue',
                        negativeButton: 'label.cancel',
                        onClose       : async (response?: DialogResponse): Promise<boolean> => {
                            if (response && response.code === DialogResponseCode.POSITIVE) {
                                // User chose YES to continue saving the form, so call onConfirmed and return the result from onConfirmed
                                return onConfirmed();
                            }

                            // User chose NOT to continue saving the form, return FALSE
                            return Promise.resolve(false);
                        },
                    }));

                    // One of the setting is removed, we don't know if the user want to continue or not yet so return FALSE
                    // for now until we get a confirmation
                    return Promise.resolve(false);
                }

                // Setting changed but NOT removed so no need to confirm, just call onConfirmed and return the result from onConfirmed
                return onConfirmed();
            }

            // setting is the same, don't need to call API.
            closeEditing();
            return Promise.resolve(true);
        }

        // Input contains error, return false
        return Promise.resolve(false);

    }, [project, rcProjectSettings, closeEditing, rcDataDispatch, uiDispatch]);



    /**
     * Create InputSettings for the RobotCloud project settings component
     */
    const rcFieldsSettings = useMemo((): RCTicketSystemSettingsCompProps['fields'] | undefined => {
        if (rcProjectSettings) {
            return {
                supportedTicketSystems: {
                    salesforce: {
                        clientId: {
                            label       : capitalizeAll(trans('ticket_system_settings.sf.client_id')),
                            displayValue: rcProjectSettings.supportedTicketSystems?.salesForceSettings?.clientId,
                            value       : rcProjectSettings.supportedTicketSystems?.salesForceSettings?.clientId,
                            error       : rcProjectSettingInputErrors.salesforceClientId
                                ? capitalizeFirst(trans(rcProjectSettingInputErrors.salesforceClientId)) : undefined,
                            editable: true,
                        },
                        clientSecret: {
                            label       : capitalizeAll(trans('ticket_system_settings.sf.client_secret')),
                            displayValue: rcProjectSettings.supportedTicketSystems?.salesForceSettings?.clientSecret,
                            value       : rcProjectSettings.supportedTicketSystems?.salesForceSettings?.clientSecret,
                            error       : rcProjectSettingInputErrors.salesforceClientSecret
                                ? capitalizeFirst(trans(rcProjectSettingInputErrors.salesforceClientSecret)) : undefined,
                            editable: true,
                        },
                        username: {
                            label       : capitalizeAll(trans('ticket_system_settings.sf.username')),
                            displayValue: rcProjectSettings.supportedTicketSystems?.salesForceSettings?.username,
                            value       : rcProjectSettings.supportedTicketSystems?.salesForceSettings?.username,
                            error       : rcProjectSettingInputErrors.salesforceUsername
                                ? capitalizeFirst(trans(rcProjectSettingInputErrors.salesforceUsername)) : undefined,
                            editable: true,
                        },
                        password: {
                            label       : capitalizeAll(trans('ticket_system_settings.sf.password')),
                            displayValue: rcProjectSettings.supportedTicketSystems?.salesForceSettings?.password,
                            value       : rcProjectSettings.supportedTicketSystems?.salesForceSettings?.password,
                            error       : rcProjectSettingInputErrors.salesforcePassword
                                ? capitalizeFirst(trans(rcProjectSettingInputErrors.salesforcePassword)) : undefined,
                            editable: true,
                        },
                        mappingObject: {
                            label       : capitalizeAll(trans('ticket_system_settings.sf.mapping_object')),
                            displayValue: rcProjectSettings.supportedTicketSystems?.salesForceSettings?.mapping?.sObject,
                            value       : rcProjectSettings.supportedTicketSystems?.salesForceSettings?.mapping?.sObject,
                            error       : rcProjectSettingInputErrors.salesforceMappingObject
                                ? capitalizeFirst(trans(rcProjectSettingInputErrors.salesforceMappingObject)) : undefined,
                            placeholder: trans('ticket_system_settings.sf.mappings_placeholder.object'),
                            editable   : true,
                        },
                        mappingDescription: {
                            label       : capitalizeAll(trans('ticket_system_settings.sf.mapping_description')),
                            displayValue: rcProjectSettings.supportedTicketSystems?.salesForceSettings?.mapping?.description,
                            value       : rcProjectSettings.supportedTicketSystems?.salesForceSettings?.mapping?.description,
                            error       : rcProjectSettingInputErrors.salesforceMappingDescription
                                ? capitalizeFirst(trans(rcProjectSettingInputErrors.salesforceMappingDescription)) : undefined,
                            placeholder: trans('ticket_system_settings.sf.mappings_placeholder.description'),
                            editable   : true,
                        },
                        mappingSeverity: {
                            label       : capitalizeAll(trans('ticket_system_settings.sf.mapping_severity')),
                            displayValue: rcProjectSettings.supportedTicketSystems?.salesForceSettings?.mapping?.severity,
                            value       : rcProjectSettings.supportedTicketSystems?.salesForceSettings?.mapping?.severity,
                            error       : rcProjectSettingInputErrors.salesforceMappingSeverity
                                ? capitalizeFirst(trans(rcProjectSettingInputErrors.salesforceMappingSeverity)) : undefined,
                            placeholder: trans('ticket_system_settings.sf.mappings_placeholder.severity'),
                            editable   : true,
                        },
                        mappingOriginValue: {
                            label       : capitalizeAll(trans('ticket_system_settings.sf.mapping_origin_value')),
                            displayValue: rcProjectSettings.supportedTicketSystems?.salesForceSettings?.mapping?.originValue,
                            value       : rcProjectSettings.supportedTicketSystems?.salesForceSettings?.mapping?.originValue,
                            error       : rcProjectSettingInputErrors.salesforceMappingOriginValue
                                ? capitalizeFirst(trans(rcProjectSettingInputErrors.salesforceMappingOriginValue)) : undefined,
                            placeholder: trans('ticket_system_settings.sf.mappings_placeholder.origin_value'),
                            editable   : true,
                        },

                        enabled: {
                            label: 'enabled',
                            value: Boolean(rcProjectSettings.supportedTicketSystems.salesForceSettings.clientId
                                    && rcProjectSettings.supportedTicketSystems.salesForceSettings.clientSecret
                                    && rcProjectSettings.supportedTicketSystems.salesForceSettings.username
                                    && rcProjectSettings.supportedTicketSystems.salesForceSettings.password),
                        },
                    },
                    asana: {
                        enabled: {
                            label: 'enabled',
                            value: Boolean(rcProjectSettings.supportedTicketSystems.asanaSettings),
                        },
                    },
                    jira: {
                        enabled: {
                            label: 'enabled',
                            value: Boolean(rcProjectSettings.supportedTicketSystems.jiraSettings),
                        },
                    },
                },
            };
        }

        return undefined;
    }, [trans, rcProjectSettings, rcProjectSettingInputErrors]);



    const onAddRemoveSupportedTicketSystem = useCallback(() => {
        onChangeEditingMode(true);
    }, [onChangeEditingMode]);


    return (
        <>
            {project && appSettings && rcFieldsSettings ? (
                <RCTicketSystemSettingsComp
                    className={clsx(isEditing && 'editing')}
                    isEditing={isEditing}
                    title={capitalizeAll(trans('label.ticket_system_settings'))}
                    icon="fas fa-ticket-alt"
                    fields={rcFieldsSettings}
                    texts={{
                        ticketSystemSettingsNoSystemMessage: capitalizeFirst(trans('ticket_system_settings.no_system_message')),
                        ticketSystemSettingsTitle          : capitalizeFirst(trans('ticket_system_settings.title')),
                        salesforceSettingsTitle            : capitalizeFirst(trans('ticket_system_settings.sf.title')),
                        asanaSettingsTitle                 : capitalizeFirst(trans('ticket_system_settings.asana.title')),
                        jiraSettingsTitle                  : capitalizeFirst(trans('ticket_system_settings.jira.title')),
                        salesforceSettingsMappingTitle     : capitalizeFirst(trans('ticket_system_settings.sf.mappings')),
                        setUpSalesforce                    : capitalizeAll(trans('ticket_system_settings.sf.set_up')),
                        setUpAsana                         : capitalizeAll(trans('ticket_system_settings.asana.set_up')),
                        setUpJira                          : capitalizeAll(trans('ticket_system_settings.jira.set_up')),
                    }}
                    addSalesforceTicketSystem={onAddRemoveSupportedTicketSystem}
                    removeSalesforceTicketSystem={onAddRemoveSupportedTicketSystem}
                    addAsanaTicketSystem={onAddRemoveSupportedTicketSystem}
                    removeAsanaTicketSystem={onAddRemoveSupportedTicketSystem}
                    addJiraTicketSystem={onAddRemoveSupportedTicketSystem}
                    removeJiraTicketSystem={onAddRemoveSupportedTicketSystem}

                    actions={permissions.canUpdateProject ? {
                        startEdit: {
                            onClick: () => {
                                onChangeEditingMode(true);
                            },
                        },
                        cancelEdit: {
                            label  : capitalizeAll(trans('label.cancel')),
                            onClick: onCancelEdit,
                        },
                        saveUpdate: {
                            label  : capitalizeAll(trans('label.save')),
                            onClick: onRobotCloudProjectInfoFormSubmit,
                        },
                    } : undefined}
                    onFieldClicked={() => {
                        onChangeEditingMode(true);
                    }}
                    onFormInputChange={onRobotCloudProjectInfoFormInputChange}
                />
            ) : (
                <CircularProgress />
            )}
        </>
    );
};
